import { Injectable } from '@angular/core';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { Store } from '@ngxs/store';
import { Observable, Subject, Subscription } from 'rxjs';
import { DocumentDistributeDialogComponent } from '@Work/Accounts/proprietary/dialogs/document-distribute-dialog/document-distribute-dialog.component';
import { cloneDeep } from 'src/ep-utils';
import { DocumentDistributeObjectService } from '../../models/proprietary/document-distribute/document-distribute-object.service';
import { DocumentDistributeObjectState } from '../../models/proprietary/document-distribute/document-distribute-object.state';
import { TaskObjectService } from '../../models/proprietary/task/task-object.service';
import { AppService } from './app.service';
import { DataService } from './data.service';
import { Datasource, DataSourceStatus } from './datasource';
import { PostDisributeResults } from './document-distribute.types';

@Injectable()
export class DocumentDistributeService {
    public dsDocumentGet: Datasource;
    private dsDocumentAssemblerProcess: Datasource;
    private sendDocumentsCallback: any = null;
    private projectID: any = null;
    private dsDocumentAssemblerProcessStatusSub: Subscription;
    callingObjectCode: string;
    dsDocumentStatusSub: Subscription;

    private postDistributeResultsSubject$: Subject<PostDisributeResults> = new Subject<PostDisributeResults>();

    public postDistributeResultsSubject(): Observable<PostDisributeResults> {
        return this.postDistributeResultsSubject$.asObservable();
    }

    constructor(
        private modalService: NgbModal,
        private ds: DataService,
        private documentDistributeOS: DocumentDistributeObjectService,
        private store: Store,
        private app: AppService,
        private taskOS: TaskObjectService
    ) {
        this.dsDocumentGet = this.ds.addNewDatasource('Document', 'NO-CACHE');
        this.dsDocumentAssemblerProcess = this.ds.addNewDatasource('DocumentAssembler', 'NO-CACHE');
    }

    public openDialog(callbackFunction: any) {
        let component = DocumentDistributeDialogComponent;
        const modalRef: NgbModalRef = this.modalService.open(component, {
            centered: true,
            size: 'lg',
            backdrop: 'static'
        });

        modalRef.componentInstance.open();

        modalRef.result.then(
            (result) => {
                callbackFunction(result);
            },
            () => { }
        );
    }

    public postDistribute(docID: any, baseDocType: string, sessionId: string | null = null) {
        let distMethod: string;

        let docDistStateEntities = this.store.selectSnapshot(DocumentDistributeObjectState.entities);

        for (var i in docDistStateEntities.document) {
            distMethod = docDistStateEntities.document[i].Dist_Method;
            let currDocument = cloneDeep(docDistStateEntities.document[i]);
            if (currDocument.Base_Doc_Type != baseDocType) {
                currDocument.Base_Doc_Type = baseDocType;
                this.documentDistributeOS.updateDocument(currDocument);
            }
            if (i != docID) {
                this.documentDistributeOS.updateDocumentID(i, docID);
            }
            break; // only one document supported by this method -- support multiple later?
        }

        if (distMethod == 'P') {
            if (this.dsDocumentStatusSub) {
                this.dsDocumentStatusSub.unsubscribe();
            }
            this.dsDocumentGet.reset(); //so that current "READY" status won't fire for new subscription
            this.dsDocumentStatusSub = this.dsDocumentGet.status$.subscribe((status) => {
                this.print(status);
                // Emit an empty array so the calling service knows there are no post distribute tasks to handle
                this.postDistributeResultsSubject$.next({ isComplete: true, taskIDs: [] });
            });

            this.dsDocumentGet.get({ json: 'CT2', Doc_ID: docID + '.pdf', View: 'Full' });
        }

        if (distMethod == 'C') {
            this.dsDocumentAssemblerProcess.reset();
            this.assembleDocument(sessionId);
        }

        if (distMethod == 'Q' || distMethod == 'F') {
            // Emit an empty array so the calling service knows there are no post distribute tasks to handle
            this.postDistributeResultsSubject$.next({ isComplete: true, taskIDs: [] });
        }
    }

    private print(status: DataSourceStatus) {
        var iframeElement: HTMLIFrameElement;

        switch (status) {
            case DataSourceStatus.Ready:
                if (this.dsDocumentStatusSub) {
                    this.dsDocumentStatusSub.unsubscribe();
                }

                let elements = document.getElementsByName('PrintElement');

                if (elements.length === 0) {
                    iframeElement = document.createElement('iframe');
                    iframeElement.setAttribute('style', 'visibility: hidden; height: 0; width: 0; position: absolute; border: 0');
                    iframeElement.name = 'PrintElement';
                    iframeElement.src = this.dsDocumentGet.blobURL;
                    document.body.appendChild(iframeElement);
                } else {
                    iframeElement = <HTMLIFrameElement>elements[0];
                    iframeElement.src = this.dsDocumentGet.blobURL;
                }

                return new Promise<void>((resolve) => {
                    const loadListener = function () {
                        iframeElement.contentWindow.focus();
                        iframeElement.contentWindow.print();
                        iframeElement.removeEventListener('load', loadListener);

                        resolve();
                    };

                    iframeElement.addEventListener('load', loadListener);
                });
            case DataSourceStatus.Error:
                if (this.dsDocumentStatusSub) {
                    this.dsDocumentStatusSub.unsubscribe();
                }
                break;
        }
    }

    assembleDocument(sessionId: string | null) {
        let docID = '';
        let printer = '';
        let docDistStateEntities = this.store.selectSnapshot(DocumentDistributeObjectState.entities);

        for (var i in docDistStateEntities.document) {
            docID = docDistStateEntities.document[i].Doc_ID;
            break; // only one document supported by this method -- support multiple later?
        }

        if (docDistStateEntities.correspondOptions.Delivery_Method == 'M') {
            printer = '[AA-DEFAULT]';
        }

        let body = {
            ConceptOneRequest: {
                SDK_Version: this.app.dsSystemInfo.data.ConceptOneReply.SDK_Version,
                DocumentAssemblerProcess: {
                    Session_ID: sessionId ?? this.taskOS.currentProjectID,
                    Book_Entity_ID: this.app.dsSystemInfo.objectNode.Context.Book.Entity_ID,
                    Book_Loc_ID: this.app.dsSystemInfo.objectNode.Context.Book_Loc.Entity_ID,
                    Assemble_For_Object_Code: 'Document',
                    Assemble_For_Object_ID: docID,
                    DocumentAssembler: {
                        Cover_Memo_Object_Code: docDistStateEntities.correspondOptions.Cover_Memo_Object_Code,
                        Delivery_Method: docDistStateEntities.correspondOptions.Delivery_Method,
                        Subject: docDistStateEntities.correspondOptions.Subject,
                        Message: docDistStateEntities.correspondOptions.Message,
                        Target_Printer: printer
                    }
                }
            }
        };

        if (this.dsDocumentAssemblerProcessStatusSub) {
            this.dsDocumentAssemblerProcessStatusSub.unsubscribe();
        }

        this.dsDocumentAssemblerProcess.process({}, body);
        this.dsDocumentAssemblerProcessStatusSub = this.dsDocumentAssemblerProcess.status$.subscribe((x) => {
            if (x == DataSourceStatus.Ready) {
                this.documentAssemblerProcessSuccessful();
            }
            if (x == DataSourceStatus.Error) {
                this.documentAssemblerProcessFailed();
            }
        });
    }

    private documentAssemblerProcessSuccessful() {
        if (this.dsDocumentAssemblerProcessStatusSub) {
            this.dsDocumentAssemblerProcessStatusSub.unsubscribe();
        }

        let taskIDs: string[] = [];

        // Emit taskIDs of correspond(s) created by DocumentAssemblerProcess for the calling service to handle
        if (this.dsDocumentAssemblerProcess.tranNode.SubTransactions?.CorrespondProcess) {
            for (var i in this.dsDocumentAssemblerProcess.tranNode.SubTransactions.CorrespondProcess) {
                taskIDs.push(this.dsDocumentAssemblerProcess.tranNode.SubTransactions.CorrespondProcess[i].Task_ID);
            }
        }

        this.postDistributeResultsSubject$.next({ isComplete: true, taskIDs: taskIDs });
    }

    private documentAssemblerProcessFailed() {
        if (this.dsDocumentAssemblerProcessStatusSub) {
            this.dsDocumentAssemblerProcessStatusSub.unsubscribe();
        }
        this.sendDocumentsCallback = null;
        this.postDistributeResultsSubject$.next({ isComplete: true, taskIDs: [] });
    }
}
